/**
 * ResumeMetric - Experience Calculator
 * Pure, deterministic date math with overlap merging.
 * @module tools/experienceCalculator
 */

function parseCalendarDate(dateStr) {
    if (!dateStr) return null;
    const [y, m, d] = dateStr.split('-').map(Number);
    if (!y || !m || !d) return null;
    // Construct as local calendar date to avoid UTC timezone drift
    return new Date(y, m - 1, d);
}

function addDays(date, days) {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
}

function daysBetween(startDate, endDate) {
    const msPerDay = 1000 * 60 * 60 * 24;
    const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
    const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());
    return Math.round((end - start) / msPerDay);
}

function getExactDuration(startDate, endDate) {
    let start = new Date(startDate);
    let end = new Date(endDate);

    if (start > end) [start, end] = [end, start];

    let years = end.getFullYear() - start.getFullYear();
    let months = end.getMonth() - start.getMonth();
    let days = end.getDate() - start.getDate();

    if (days < 0) {
        months--;
        const prevMonth = new Date(end.getFullYear(), end.getMonth(), 0);
        days += prevMonth.getDate();
    }

    if (months < 0) {
        years--;
        months += 12;
    }

    return { years, months, days };
}

export function calculateExperience(periods) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const normalized = periods.map(p => {
        const start = parseCalendarDate(p.startDate);
        const end = p.isPresent ? today : parseCalendarDate(p.endDate);
        if (!start || !end || start > end) return null;
        return { start, end, isPresent: p.isPresent };
    }).filter(Boolean);

    if (normalized.length === 0) {
        return { 
            total: { years: 0, months: 0, days: 0, decimalYears: 0, totalDays: 0 }, 
            mergedIntervals: [], 
            gaps: [] 
        };
    }

    normalized.sort((a, b) => a.start - b.start);

    const merged = [normalized[0]];
    for (let i = 1; i < normalized.length; i++) {
        const last = merged[merged.length - 1];
        const curr = normalized[i];
        if (curr.start <= last.end) {
            last.end = new Date(Math.max(last.end.getTime(), curr.end.getTime()));
        } else {
            merged.push(curr);
        }
    }

    let totalDays = 0;
    const results = merged.map(m => {
        const duration = getExactDuration(m.start, m.end);
        totalDays += daysBetween(m.start, m.end) + 1; 
        return { start: m.start, end: m.end, isPresent: m.isPresent, duration };
    });

    const gaps = [];
    for (let i = 1; i < merged.length; i++) {
        const gapStart = addDays(merged[i-1].end, 1);
        const gapEnd = addDays(merged[i].start, -1);
        if (gapStart <= gapEnd) {
            gaps.push({ start: gapStart, end: gapEnd, duration: getExactDuration(gapStart, gapEnd) });
        }
    }

    // Convert accumulated days to Y/M/D using average lengths to avoid calendar projection
    const decimalYears = totalDays / 365.25;
    const years = Math.floor(decimalYears);
    const remainingDays = totalDays - (years * 365.25);
    const months = Math.floor(remainingDays / 30.4375);
    const days = Math.round(remainingDays - (months * 30.4375));

    return {
        total: { years, months, days, decimalYears, totalDays },
        mergedIntervals: results,
        gaps
    };
}
